import { z } from "zod/v4";
import { defineEndpoint, defineResource } from "@visioni/a3s-client-api";
import { AssetCameraKeySchema, SiteIdCodec } from "@visioni/a3s-schema/codecs";
import { stringToInt } from "@visioni/a3s-codecs";
import { FEATURE_NAME } from "../feature-name";

/**
 * Patriot activations — the tickets an a3s-marked signal raised and an operator
 * attended — listed at whichever level of the fleet the caller is looking at.
 *
 * ## What a caller sees, and what it deliberately does not
 *
 * The routes serving these are support-only today, so the allowlist is not the
 * access control — it is the contract that lets the shape be widened to tenant
 * users without re-auditing every field Patriot happens to return.
 *
 * A Patriot activation carries far more than a customer should read: the
 * monitoring operator's name, the contact roster Patriot dialled (names, phone
 * numbers, addresses), Patriot-internal identifiers, and raw device payloads.
 * The schemas below are an **allowlist**, not a trim of the stored record — a
 * field reaches a customer only by being named here.
 *
 * Excluded on purpose:
 *
 * - `operatorName` / `operatorResponseTime` on the activation — identifies a
 *   Vision operator and measures them. Not the customer's business. A note's
 *   `operatorLogin` is carried for support, to tell a3s's own notes from an
 *   operator's; drop it before opening the routes to tenant users.
 * - The contact roster Patriot dialled is not replicated at all.
 * - `clientNo` / `clientName` — Patriot's own account identity. A customer
 *   knows their camera; the Patriot client number is a mapping detail. The
 *   fleet shapes, served only to support, carry `clientNo`.
 * - `rawData` / `media` on a signal, and `concurrencyFlag` / `typeNo` —
 *   device payloads and internal plumbing.
 *
 * Named **activation** throughout, which is Patriot's own word for the record.
 * It is also the unambiguous one: `a3s-alarm-schedules` and the app already own
 * `alarm` for arming windows, down to an `AlarmDetail` of their own, so calling
 * this an alarm would have collided with a live concept in the same codebase.
 */

/**
 * One row of an activation list.
 *
 * **Patriot's fields keep Patriot's names**, `*DateTime` suffixes included;
 * a3s-owned fields are `a3s`-prefixed. The replica is a faithful copy, so a
 * second vocabulary for the same values is a translation layer somebody has to
 * keep correct forever.
 *
 * `pendingState` and `completedStatus` **co-exist** on one activation, so
 * neither is derivable from the other and both are carried.
 */
export const ActivationSummary = z.object({
  /** Patriot's ticket number — the reference an operator and a customer both quote. */
  ticketNo: z.string(),
  /** When Patriot created the activation, normalised to UTC. */
  createdDateTime: z.string(),
  /** Patriot's lifecycle state (`AttendPendingState`), while there is one. */
  pendingState: z.string().optional(),
  /** Patriot's terminal status (`AlarmCompleteStatus`: `COMPLETED`, `CANCELLED`, …), once there is one. */
  completedStatus: z.string().optional(),
  /** Operator-facing summary line, e.g. `Vehicle Detected`. */
  description: z.string().optional(),
  /** Patriot's completion reason, e.g. `Cloud Cover/Exposure`. */
  reason: z.string().optional(),
  /** Patriot priority; lower is more urgent. */
  priority: z.number().optional(),
  /**
   * Where the a3s marker placed it. Present together or not at all, and carried
   * on every row because an unfiltered list spans the whole tenant — a row
   * saying only `camera-a` would not identify anything there.
   *
   * Prefixed although Patriot's `Activation` has no field of these names today:
   * Patriot uses `site` across `Client` for the *monitored premises*, which is
   * not an a3s site, so a future `Activation.site` would be a different thing
   * under the same name.
   */
  a3sSite: z.string().optional(),
  a3sAsset: z.string().optional(),
  a3sCamera: z.string().optional(),
});
export type ActivationSummary = z.output<typeof ActivationSummary>;

export const ListTenantActivationsResponse = z.object({
  items: z.array(ActivationSummary),
  /** Opaque resume token; `null` when the list is exhausted. Pass back verbatim. */
  cursor: z.string().nullable(),
});
export type ListTenantActivationsResponse = z.output<typeof ListTenantActivationsResponse>;

/**
 * Which end of a list's time order it starts from: `desc` (newest first, the
 * default when absent) or `asc` (oldest first). A cursor resumes only the order
 * it was issued for.
 */
export const ListOrder = z.enum(["asc", "desc"]);
export type ListOrder = z.output<typeof ListOrder>;

/**
 * `site` and `camera` narrow the list the same way `since` / `until` do — they
 * are filters, not a route. Absent, the list spans the tenant.
 *
 * `limit` defaults rather than staying optional all the way down: left unset the
 * query carries no `Limit` and DynamoDB answers with a full 1MB page. `since` /
 * `until` are inclusive instants, in any offset, against the activation's raised
 * time.
 */
export const ListTenantActivationsQueryParams = z
  .object({
    limit: stringToInt.pipe(z.int().positive().max(100)).default(20),
    cursor: z.string().optional(),
    since: z.iso.datetime({ offset: true }).optional(),
    until: z.iso.datetime({ offset: true }).optional(),
    order: ListOrder.optional(),
    site: SiteIdCodec.optional(),
    camera: AssetCameraKeySchema.optional(),
  })
  // The replica indexes a camera under its site, so there is no partition
  // holding one camera across every site. Rejected at the codec rather than
  // silently widening to the tenant, which would answer a question the caller
  // did not ask.
  .refine((q) => !(q.camera !== undefined && q.site === undefined), {
    message: "camera cannot be filtered without site",
    path: ["camera"],
  })
  .refine((q) => q.since === undefined || q.until === undefined || Date.parse(q.since) <= Date.parse(q.until), {
    message: "until cannot be before since",
    path: ["until"],
  });
export type ListTenantActivationsQueryParamsWire = z.input<typeof ListTenantActivationsQueryParams>;
export type ListTenantActivationsQueryParams = z.output<typeof ListTenantActivationsQueryParams>;

/**
 * ## Paths
 *
 * Everything Patriot-derived sits under a `patriot/` segment, so the replica's
 * surface is one subtree a reader can take in at once rather than routes mixed
 * in among a3s's own.
 *
 * ```
 * /features/monitoring/patriot/tenant/activations[?site=&camera=]
 * /features/monitoring/patriot/tenant/activations/{ticketNo}
 * /features/monitoring/patriot/fleet/activations
 * /features/monitoring/patriot/fleet/activations/{ticketNo}
 * ```
 *
 * The `tenant/` or `fleet/` segment names the scope a route reads, so the two
 * cannot be mistaken for each other in a log or a link.
 *
 * **Within the tenant scope, one list endpoint narrowed by query params
 * rather than a route per width.** The widths are the same list read at different depths of the same
 * hierarchy, so they share a response shape, a cursor and a client hook —
 * splitting them across routes only forced callers to choose a URL by which
 * filters they happened to hold. Keeping them together also makes the cache
 * key group them: every view shares `resource`, differing only in `query`, so
 * one invalidation covers all of them.
 *
 * All are declared `scope: "tenant"` and spell out their own tail. That is
 * not a claim about who may call them — all are support-only. It is because
 * the shared camera scope hard-codes `sites/{siteId}/cameras/…` directly after
 * the feature name, which leaves nowhere to put `patriot/`.
 */
export const tenantActivationsResource = defineResource({
  feature: FEATURE_NAME,
  resource: "patriot/tenant/activations",
  scope: "tenant",
});

export const listTenantActivationsEndpoint = defineEndpoint(tenantActivationsResource, {
  responseSchema: ListTenantActivationsResponse,
  querySchema: ListTenantActivationsQueryParams,
});

/**
 * {@link ActivationSummary} plus whose it is, since the fleet list spans
 * tenants: the tenant — absent, like every `a3s*` field, on an unattributed
 * activation — and Patriot's client number, which every activation has.
 */
export const FleetActivationSummary = ActivationSummary.extend({
  a3sTenant: z.string().optional(),
  /** Patriot's client number, e.g. `10539BASE01`. */
  clientNo: z.string().optional(),
});
export type FleetActivationSummary = z.output<typeof FleetActivationSummary>;

/**
 * `clientNo` on a fleet list — activations, operator notes, genuine incidents —
 * is the start of a client number, matched ignoring case as the client list
 * matches it: an asset id (`11238`) finds every client behind that asset
 * (`11238BASE01`, `11238BASE02`), and a whole number finds just that client.
 * A start matching more than {@link FLEET_MAX_MATCHED_CLIENTS} clients is
 * refused.
 */
export const FleetClientNoParam = z.string().trim().min(1).optional();

/**
 * How many clients a fleet list's `clientNo` may match.
 *
 * Each list's client index is partitioned by client number, so a prefix cannot
 * be a key condition: each matched client is a query of its own, and a page
 * reads every one. The cap bounds that cost. It is sized for an asset id,
 * which matches a handful of clients; a shorter prefix is refused rather than
 * fanned out across the fleet.
 */
export const FLEET_MAX_MATCHED_CLIENTS = 10;

/** The clients a fleet list's `clientNo` matched, in client-number order. Absent unfiltered; empty when none did. */
export const MatchedClientNos = z.array(z.string()).optional();

export const ListFleetActivationsResponse = z.object({
  items: z.array(FleetActivationSummary),
  /**
   * Opaque resume token; `null` when the window is exhausted. Pass back
   * verbatim. A page can come back empty with a non-null cursor — the months
   * it walked held nothing — so page until this is `null`, not until a page is
   * empty.
   */
  cursor: z.string().nullable(),
  clientNos: MatchedClientNos,
});
export type ListFleetActivationsResponse = z.output<typeof ListFleetActivationsResponse>;

/**
 * A time window, and optionally a client: the fleet index is bucketed by month
 * and knows nothing of sites or cameras, whose ids are tenant-scoped anyway.
 * `clientNo` is the start of a client number — see {@link FleetClientNoParam}.
 *
 * `since` is **required** — the index is walked month by month, and an
 * open-ended read would walk into empty history with nothing to stop it.
 */
export const ListFleetActivationsQueryParams = z
  .object({
    limit: stringToInt.pipe(z.int().positive().max(100)).default(20),
    cursor: z.string().optional(),
    since: z.iso.datetime({ offset: true }),
    until: z.iso.datetime({ offset: true }).optional(),
    order: ListOrder.optional(),
    clientNo: FleetClientNoParam,
  })
  .refine((q) => q.until === undefined || Date.parse(q.since) <= Date.parse(q.until), {
    message: "until cannot be before since",
    path: ["until"],
  });

export type ListFleetActivationsQueryParamsWire = z.input<typeof ListFleetActivationsQueryParams>;
export type ListFleetActivationsQueryParams = z.output<typeof ListFleetActivationsQueryParams>;

/**
 * Every activation in the replica, whichever tenant it belongs to — including
 * unattributed ones (Patriot's supervision alarms, other ingest paths), which
 * appear in no tenant list because the fleet reads Patriot's own month index.
 *
 * Under the caller's tenant in the path only because every client-API route is
 * — the gateway mounts the feature at `/tenants/{tenantId}/features/…`. The
 * tenant does not narrow the list; the support gate is the whole check.
 * `patriot/fleet/…` rather than a query flag on the tenant list, so a caller
 * can never widen a tenant read to the fleet by adding a parameter.
 */
export const fleetActivationsResource = defineResource({
  feature: FEATURE_NAME,
  resource: "patriot/fleet/activations",
  scope: "tenant",
});

export const listFleetActivationsEndpoint = defineEndpoint(fleetActivationsResource, {
  responseSchema: ListFleetActivationsResponse,
  querySchema: ListFleetActivationsQueryParams,
});

/**
 * The clip a3s sent a signal with, as it sits in the realm's upload bucket.
 *
 * `url` is presigned and expires an hour after the response is built, so a
 * page left open past that has to refetch. `key` is the object key, for anyone
 * reaching the file with AWS tooling instead. The file is the camera's own
 * format — an Axis clip is `.mkv`, which not every browser plays.
 */
export const ActivationSignalClip = z.object({
  key: z.string(),
  url: z.string(),
});
export type ActivationSignalClip = z.output<typeof ActivationSignalClip>;

/**
 * What the monitoring triage classifier made of a signal's clip.
 *
 * - `classified` — triage ran. `response` is the classifier's verdict, before any
 *   delivery policy remapped it. `threatScore` is the figure Patriot's description
 *   shows as a percentage; it is not the model's confidence, which is
 *   `detectionConfidence`. `outcome` and `model` are the model's own words.
 * - `test_signal` — triage is skipped for test signals.
 * - `none` — no classification was recorded: triage failed or timed out, or the
 *   provider does not triage.
 */
export const ActivationSignalTriage = z.discriminatedUnion("status", [
  z.object({
    status: z.literal("classified"),
    response: z.string(),
    summary: z.string(),
    threatScore: z.number().optional(),
    detectionConfidence: z.number().optional(),
    outcome: z.string().optional(),
    model: z.string().optional(),
  }),
  z.object({ status: z.literal("test_signal") }),
  z.object({ status: z.literal("none") }),
]);
export type ActivationSignalTriage = z.output<typeof ActivationSignalTriage>;

/**
 * How a delivered signal reached Patriot, from the delivery receipt.
 *
 * `action` is what delivery did in Patriot: `created` a new activation,
 * `aggregated` the signal into one already open, `escalated` one already open,
 * or `create_and_notify` — created one and noted it on the open one. That open
 * activation is `existingTicketNo`.
 *
 * `mapping` is absent for providers that do not classify. Where its
 * `classificationKey` and `deliveredKey` differ, a delivery policy remapped the
 * classifier's verdict — `policy` names which.
 */
export const ActivationSignalDelivery = z.object({
  action: z.string(),
  typeNo: z.string().optional(),
  existingTicketNo: z.string().optional(),
  policy: z.array(z.string()),
  mapping: z
    .object({
      response: z.string(),
      classificationKey: z.string(),
      deliveredKey: z.string(),
      deliveredTypeNo: z.string(),
      deliveredPriority: z.string(),
    })
    .optional(),
});
export type ActivationSignalDelivery = z.output<typeof ActivationSignalDelivery>;

/**
 * a3s's record of how it handled a signal, from its signal-dispatch table: what
 * triage made of the clip, how it was delivered, and how long each step took.
 *
 * `capturedAt` is the clip's own timestamp, so `receivedAt - capturedAt` is the
 * camera's upload lag and `settledAt - receivedAt` is triage plus delivery.
 * `settledAt` is when the signal reached its final `status`; absent while
 * `pending`. `failures` are the delivery attempts that failed, oldest first.
 */
export const ActivationSignalProcessing = z.object({
  provider: z.string(),
  status: z.enum(["pending", "delivered", "poisoned", "expired"]),
  capturedAt: z.string(),
  receivedAt: z.string(),
  settledAt: z.string().optional(),
  triage: ActivationSignalTriage,
  delivery: ActivationSignalDelivery.optional(),
  failures: z.array(z.object({ at: z.string(), transient: z.boolean(), reason: z.string() })),
});
export type ActivationSignalProcessing = z.output<typeof ActivationSignalProcessing>;

/**
 * One signal on an activation — every signal Patriot linked to it, whoever sent
 * it. An activation raised by another integration (a Reconeyez email, say), or
 * one a3s's signal joined, carries signals with no a3s marker; leaving them out
 * would show such an activation as raised by nothing.
 *
 * `notes` is Patriot's free text on the signal, verbatim: on an a3s signal it
 * holds the a3s marker, on another integration's whatever that integration
 * wrote. It is carried raw because this view is for investigating what Patriot
 * holds, not a customer-facing summary — review it before opening the routes to
 * tenant users.
 *
 * **`signalId` is Patriot's own id for the signal, and `a3sSignalId` is not it.**
 * `signalId` is what the signal routes take; `a3sSignalId` is the id parsed from
 * the a3s marker in `notes`, absent when a3s did not send the signal.
 *
 * `a3sClip` is the file a3s sent the signal with, read from a3s's own record of the
 * signal rather than from Patriot. Absent when there is no a3s signal ID, or when
 * this realm holds no record of it — the fleet view can open another realm's
 * activation, whose signals are in that realm's table. `a3sProcessing` comes from
 * the same record and is absent on the same terms.
 */
export const ActivationSignal = z.object({
  signalId: z.string(),
  a3sSignalId: z.string().optional(),
  receivedDateTime: z.string(),
  description: z.string().optional(),
  notes: z.string().optional(),
  a3sClip: ActivationSignalClip.optional(),
  a3sProcessing: ActivationSignalProcessing.optional(),
});
export type ActivationSignal = z.output<typeof ActivationSignal>;

/**
 * One operator note on an activation.
 *
 * **Free text written by a monitoring operator**, not composed for the customer.
 * It is surfaced because the activation's story is otherwise just timestamps, but it
 * is the one field here whose content nobody has reviewed — see the module
 * comment's allowlist rationale.
 *
 * Named in full because Patriot calls three unrelated fields `notes`; the type
 * is what disambiguates the one below. See `StoredOperatorNote` in
 * `client-api/activations/project.ts`.
 */
export const ActivationOperatorNote = z.object({
  noteId: z.string(),
  noteDateTime: z.string(),
  /** Patriot's `NoteType`: `OPERATOR_PICKED_UP`, `CAMERA_ACTION`, `PATROLS_CALLED_ACTION`, `ACTIVATION_COMPLETED`, … */
  noteType: z.string().optional(),
  /** Patriot's title for the note. On a call it names the contact dialled. */
  description: z.string().optional(),
  notes: z.string().optional(),
  /** The Patriot login that wrote the note — a Vision operator, or a3s's own monitoring account. */
  operatorLogin: z.string().optional(),
});
export type ActivationOperatorNote = z.output<typeof ActivationOperatorNote>;

/**
 * One activation in full: the summary, the handling timeline, and what it was
 * assembled from.
 *
 * `respondedDateTime` is when an operator picked it up, `completedDateTime` when
 * they finished. Both absent means nobody has attended it yet.
 */
export const ActivationDetail = ActivationSummary.extend({
  respondedDateTime: z.string().optional(),
  completedDateTime: z.string().optional(),
  signals: z.array(ActivationSignal),
  operatorNotes: z.array(ActivationOperatorNote),
});
export type ActivationDetail = z.output<typeof ActivationDetail>;

export const GetActivationPathParams = z.object({ ticketNo: z.string().min(1) });
export type GetActivationPathParams = z.output<typeof GetActivationPathParams>;

/**
 * A ticket number is unique across Patriot, so the detail route needs no
 * placement in its path. What keeps one tenant out of another's activation is the
 * check that the activation's own marker names the tenant in the path prefix —
 * see the isolation note in `client-api/activations/handler.ts`.
 */
export const tenantActivationResource = defineResource({
  feature: FEATURE_NAME,
  resource: "patriot/tenant/activations/{ticketNo}",
  scope: "tenant",
  resourcePathSchema: GetActivationPathParams,
});

export const getTenantActivationEndpoint = defineEndpoint(tenantActivationResource, {
  responseSchema: ActivationDetail,
});

/**
 * {@link ActivationDetail} plus the tenant it belongs to, as
 * {@link FleetActivationSummary}. Same allowlist, so `signals` carries only
 * the a3s-marked ones — an unattributed activation shows none.
 */
export const FleetActivationDetail = ActivationDetail.extend({
  a3sTenant: z.string().optional(),
});
export type FleetActivationDetail = z.output<typeof FleetActivationDetail>;

/**
 * Any activation in the replica by ticket, whichever tenant holds it — or none.
 *
 * Unlike the tenant detail route, there is no marker check: the fleet list
 * links here for unattributed activations and for tenants the caller's
 * directory may not hold (an int stack replicating prod's Patriot). The
 * support gate is the whole check, as for the fleet list.
 */
export const fleetActivationResource = defineResource({
  feature: FEATURE_NAME,
  resource: "patriot/fleet/activations/{ticketNo}",
  scope: "tenant",
  resourcePathSchema: GetActivationPathParams,
});

export const getFleetActivationEndpoint = defineEndpoint(fleetActivationResource, {
  responseSchema: FleetActivationDetail,
});
